import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  FolderIcon,
  UsersIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { docConvertAPI, dailyNoteImageUrl, downloadDailyNoteDocument } from '../../api/docConvertApi';
import ImagePreview from '../../components/image/ImagePreview';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import ResponsiveModal from '../../components/ui/ResponsiveModal';
import { translateDepartment } from './departmentLabels';
import './notesDashboard.css';

const COLORS = [
  '#0D9488', '#F59E0B', '#6366F1', '#EC4899', '#10B981',
  '#3B82F6', '#EF4444', '#8B5CF6', '#14B8A6', '#F97316',
  '#06B6D4', '#D946EF', '#84CC16', '#E11D48', '#0EA5E9',
];

const STATUS_MAP = {
  open: { label: 'Açık', color: '#3B82F6' },
  closed: { label: 'Kapatılan', color: '#10B981' },
  returned: { label: 'Dönüş Yapılan', color: '#F59E0B' },
};

const STATUS_LABEL_TO_KEY = Object.fromEntries(
  Object.entries(STATUS_MAP).map(([key, value]) => [value.label, key])
);

const LIMIT_OPTIONS = [10, 20, 30, 0];

const NOTE_TEXTAREA_CLASS =
  'w-full min-h-[11rem] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white resize-y';

const READONLY_TEXTAREA_CLASS = `${NOTE_TEXTAREA_CLASS} bg-gray-50 dark:bg-gray-800 cursor-default`;

const getNoteImages = (row) => row?.note_images ?? row?.images ?? [];
const getReturnImages = (row) => row?.return_images ?? [];
const getNoteDocuments = (row) => row?.note_documents ?? [];
const getReturnDocuments = (row) => row?.return_documents ?? [];

const toPreviewImages = (images = []) =>
  images.map((image) => ({
    id: image.id,
    name: image.original_name || 'Görsel',
    preview: dailyNoteImageUrl(image.id),
    thumbnail_url: dailyNoteImageUrl(image.id),
  }));

function ReadOnlyImages({ label, images = [], onOpenPreview }) {
  return (
    <div className="space-y-1 md:col-span-2">
      <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">{label}</Label>
      {images.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {images.map((image, index) => (
            <button
              key={image.id ?? index}
              type="button"
              onClick={() => onOpenPreview(images, index)}
              className="h-16 w-16 overflow-hidden rounded border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 hover:ring-2 hover:ring-highlight-500"
              title={image.original_name || 'Görseli büyüt'}
            >
              <img
                src={dailyNoteImageUrl(image.id)}
                alt={image.original_name || 'Görsel'}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-400 dark:text-gray-500">Dosya seçilmedi</p>
      )}
    </div>
  );
}

function ReadOnlyDocuments({ label, documents = [] }) {
  return (
    <div className="space-y-1 md:col-span-2">
      <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">{label}</Label>
      {documents.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          {documents.map((document) => (
            <button
              key={document.id}
              type="button"
              onClick={async () => {
                try {
                  await downloadDailyNoteDocument(document.id, document.original_name);
                } catch {
                  // ignore download errors in preview
                }
              }}
              className="inline-flex max-w-full items-center gap-1.5 rounded border border-gray-200 px-2.5 py-1.5 text-xs text-blue-600 hover:bg-gray-50 dark:border-gray-600 dark:text-blue-400 dark:hover:bg-gray-700"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{document.original_name || 'Döküman'}</span>
            </button>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-400 dark:text-gray-500">Dosya seçilmedi</p>
      )}
    </div>
  );
}

function normalizeStatus(raw = '') {
  const lower = String(raw).trim().toLocaleLowerCase('tr-TR');
  if (!lower) return 'open';
  if (['kapatıldı', 'kapalı', 'kapandı', 'tamamlandı', 'closed', 'kapali'].some((k) => lower.includes(k))) return 'closed';
  if (['dönüş', 'donus', 'döndü', 'returned', 'cevap'].some((k) => lower.includes(k))) return 'returned';
  if (['açık', 'acik', 'open', 'bekliyor', 'devam'].some((k) => lower.includes(k))) return 'open';
  return 'open';
}

function noteHasReturn(note) {
  return Boolean((note.donusler || '').trim());
}

function noteTitle(note) {
  return (note.not_basligi || '').trim() || 'Başlıksız';
}

function noteDepartment(note) {
  return (note.ilgili_departman || '').trim() || 'Belirtilmemiş';
}

function matchNotes(notes, filter) {
  if (!filter) return [];
  return notes.filter((note) => {
    switch (filter.type) {
      case 'all':
        return true;
      case 'title':
        return noteTitle(note) === filter.value;
      case 'notAdi':
        return (note.ilgili_not_adi || '').trim() === filter.value;
      case 'department': {
        if (noteDepartment(note) !== filter.value) return false;
        if (filter.returnStatus === 'with') return noteHasReturn(note);
        if (filter.returnStatus === 'without') return !noteHasReturn(note);
        return true;
      }
      case 'status':
        return normalizeStatus(note.durum) === filter.value;
      case 'manager':
        return (note.ilgili_kisi || '').trim() === filter.value;
      case 'returnPerson':
        return noteHasReturn(note) && ((note.ilgili_kisi || '').trim() || 'Bilinmeyen') === filter.value;
      case 'withReturn':
        return noteHasReturn(note);
      case 'withoutReturn':
        return !noteHasReturn(note);
      default:
        return false;
    }
  });
}

function buildNotesPageUrl(filter) {
  const params = new URLSearchParams();
  if (!filter) return '/internal-audit/doc-convert/notes';

  switch (filter.type) {
    case 'title':
      if (filter.value && filter.value !== 'Başlıksız') params.set('search', filter.value);
      break;
    case 'notAdi':
      params.set('ilgili_not_adi', filter.value);
      break;
    case 'department':
      if (filter.value !== 'Belirtilmemiş') params.set('ilgili_departman', filter.value);
      if (filter.returnStatus === 'with') params.set('has_return', '1');
      if (filter.returnStatus === 'without') params.set('has_return', '0');
      break;
    case 'manager':
    case 'returnPerson':
      if (filter.value && filter.value !== 'Bilinmeyen') params.set('ilgili_kisi', filter.value);
      if (filter.type === 'returnPerson') params.set('has_return', '1');
      break;
    case 'withReturn':
      params.set('has_return', '1');
      break;
    case 'withoutReturn':
      params.set('has_return', '0');
      break;
    default:
      break;
  }

  const qs = params.toString();
  return qs ? `/internal-audit/doc-convert/notes?${qs}` : '/internal-audit/doc-convert/notes';
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-1 font-semibold text-slate-700 dark:text-slate-100">{translateDepartment(label)}</div>
      {payload.map((p) => (
        <div key={p.dataKey || p.name} className="flex items-center gap-2 py-0.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: p.color || p.fill }} />
          <span className="text-slate-500 dark:text-slate-300">{p.name}</span>
          <span className="ml-auto font-semibold text-slate-700 dark:text-slate-100">{p.value}</span>
        </div>
      ))}
      <div className="mt-1 border-t border-slate-100 pt-1 text-[10px] text-slate-400 dark:border-slate-700">
        Detay için tıklayın
      </div>
    </div>
  );
}

function PieTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-1 font-semibold text-slate-700 dark:text-slate-100">{p.name}</div>
      <div className="flex items-center gap-2 py-0.5">
        <span className="h-2.5 w-2.5 rounded-sm" style={{ background: p.payload?.fill }} />
        <span className="text-slate-500 dark:text-slate-300">Adet</span>
        <span className="ml-auto font-semibold text-slate-700 dark:text-slate-100">{p.value}</span>
      </div>
      <div className="mt-1 border-t border-slate-100 pt-1 text-[10px] text-slate-400 dark:border-slate-700">
        Detay için tıklayın
      </div>
    </div>
  );
}

function KpiCard({ label, value, accent, icon: Icon, onClick }) {
  const clickable = Boolean(onClick);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={`nd-kpi ${clickable ? 'nd-kpi--clickable' : ''}`}
      style={{ '--accent': accent, '--accent-soft': `${accent}18` }}
    >
      <div className="nd-kpi-top">
        <span className="nd-kpi-label">{label}</span>
        {Icon && (
          <span className="nd-kpi-icon">
            <Icon className="h-4 w-4" />
          </span>
        )}
      </div>
      <span className="nd-kpi-value">{value}</span>
      {clickable && <span className="nd-kpi-hint">Detay için tıkla</span>}
    </button>
  );
}

function ChartCard({ title, subtitle, actions = null, children }) {
  return (
    <div className="nd-card">
      <div className="nd-card-head">
        <div>
          <h3 className="nd-card-title">{title}</h3>
          {subtitle && <p className="nd-card-sub">{subtitle}</p>}
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

function LimitSelector({ value, onChange, activeColor }) {
  return (
    <div className="nd-limit">
      {LIMIT_OPTIONS.map((n) => {
        const active = value === n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={active ? 'active' : ''}
            style={active ? { background: activeColor } : undefined}
          >
            {n === 0 ? 'All' : n}
          </button>
        );
      })}
    </div>
  );
}

const RADIAN = Math.PI / 180;
function renderPieLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  if (percent < 0.04) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={700}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

function formatDateDisplay(value) {
  if (!value) return '';
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    const [y, m, d] = text.slice(0, 10).split('-');
    return `${d}.${m}.${y}`;
  }
  return text;
}

function truncate(text, max = 120) {
  const value = String(text || '').trim();
  if (!value) return '';
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

export default function NotesDashboardPage() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasRun, setHasRun] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [titleLimit, setTitleLimit] = useState(10);
  const [notAdiLimit, setNotAdiLimit] = useState(10);
  const [deptLimit, setDeptLimit] = useState(10);
  const [drillFilter, setDrillFilter] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [noteDetailLoading, setNoteDetailLoading] = useState(false);
  const [noteDetailError, setNoteDetailError] = useState(null);
  const [previewImages, setPreviewImages] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [drillSearch, setDrillSearch] = useState('');

  // Dashboard açılışta veri çekmez; koşullar seçilip "Rapor Oluştur"a basınca
  // yalnızca seçilen tarih aralığındaki notlar çekilir (büyük veri hızlı gelir).
  const runReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDrillFilter(null);
    try {
      const params = { page_size: 0 };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search.trim()) params.search = search.trim();
      const response = await docConvertAPI.getDailyNotes(params);
      setNotes(response?.data || []);
      setHasRun(true);
    } catch (err) {
      setError(err.message || 'Rapor oluşturulamadı.');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, search]);

  const openDrill = useCallback((filter) => {
    setSelectedNote(null);
    setNoteDetailError(null);
    setDrillSearch('');
    setDrillFilter(filter);
  }, []);

  const openNoteDetail = useCallback(async (note) => {
    if (!note?.id) return;
    setSelectedNote(note);
    setNoteDetailLoading(true);
    setNoteDetailError(null);
    try {
      const response = await docConvertAPI.getDailyNote(note.id);
      setSelectedNote(response?.data ?? response ?? note);
    } catch (err) {
      setNoteDetailError(err.message || 'Not detayı yüklenemedi.');
    } finally {
      setNoteDetailLoading(false);
    }
  }, []);

  const closeNoteDetail = useCallback(() => {
    setSelectedNote(null);
    setNoteDetailError(null);
    setNoteDetailLoading(false);
  }, []);

  const openImagePreview = useCallback((images, index = 0) => {
    setPreviewImages(toPreviewImages(images));
    setPreviewIndex(index);
    setIsPreviewOpen(true);
  }, []);

  const drillNotes = useMemo(
    () => matchNotes(notes, drillFilter),
    [notes, drillFilter]
  );

  const filteredDrillNotes = useMemo(() => {
    const q = drillSearch.trim().toLocaleLowerCase('tr-TR');
    if (!q) return drillNotes;
    return drillNotes.filter((note) => {
      const haystack = [
        note.not_basligi,
        note.ilgili_not_adi,
        note.ilgili_departman,
        note.ilgili_kisi,
        note.durum,
        note.not,
        note.donusler,
        note.not_tarihi,
      ]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('tr-TR');
      return haystack.includes(q);
    });
  }, [drillNotes, drillSearch]);

  const drillSummary = useMemo(() => {
    const withReturn = drillNotes.filter((n) => noteHasReturn(n)).length;
    return {
      total: drillNotes.length,
      withReturn,
      withoutReturn: drillNotes.length - withReturn,
      returnRate: drillNotes.length ? Math.round((withReturn / drillNotes.length) * 100) : 0,
    };
  }, [drillNotes]);

  const analysis = useMemo(() => {
    if (!notes.length) return null;

    const byTitle = {};
    const byNotAdi = {};
    const byManager = {};
    const byReturnPerson = {};
    const byDepartment = {};
    const statusBuckets = { open: 0, closed: 0, returned: 0 };
    let withReturnTotal = 0;
    let withoutReturnTotal = 0;

    notes.forEach((note) => {
      const title = noteTitle(note);
      byTitle[title] = (byTitle[title] || 0) + 1;

      const notAdi = (note.ilgili_not_adi || '').trim();
      if (notAdi) {
        byNotAdi[notAdi] = (byNotAdi[notAdi] || 0) + 1;
      }

      const manager = (note.ilgili_kisi || '').trim();
      if (manager) {
        byManager[manager] = (byManager[manager] || 0) + 1;
      }

      const status = normalizeStatus(note.durum);
      statusBuckets[status] = (statusBuckets[status] || 0) + 1;

      const hasReturn = noteHasReturn(note);
      if (hasReturn) {
        withReturnTotal += 1;
        const person = manager || 'Bilinmeyen';
        byReturnPerson[person] = (byReturnPerson[person] || 0) + 1;
      } else {
        withoutReturnTotal += 1;
      }

      const department = noteDepartment(note);
      if (!byDepartment[department]) {
        byDepartment[department] = { total: 0, withReturn: 0, withoutReturn: 0 };
      }
      byDepartment[department].total += 1;
      if (hasReturn) {
        byDepartment[department].withReturn += 1;
      } else {
        byDepartment[department].withoutReturn += 1;
      }
    });

    const titleDataAll = Object.entries(byTitle)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    const titleData = titleLimit === 0 ? titleDataAll : titleDataAll.slice(0, titleLimit);

    const notAdiDataAll = Object.entries(byNotAdi)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    const notAdiData = notAdiLimit === 0 ? notAdiDataAll : notAdiDataAll.slice(0, notAdiLimit);

    const departmentDataAll = Object.entries(byDepartment)
      .map(([name, stats]) => ({
        name,
        total: stats.total,
        withReturn: stats.withReturn,
        withoutReturn: stats.withoutReturn,
      }))
      .sort((a, b) => b.total - a.total);

    const departmentData = deptLimit === 0 ? departmentDataAll : departmentDataAll.slice(0, deptLimit);

    const managerData = Object.entries(byManager)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);

    const returnPersonData = Object.entries(byReturnPerson)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);

    const statusData = Object.entries(statusBuckets)
      .filter(([, count]) => count > 0)
      .map(([key, count]) => ({
        key,
        name: STATUS_MAP[key]?.label || key,
        value: count,
        fill: STATUS_MAP[key]?.color || '#94A3B8',
      }));

    return {
      total: notes.length,
      uniqueTitles: Object.keys(byTitle).length,
      uniqueNotAdi: Object.keys(byNotAdi).length,
      uniqueManagers: Object.keys(byManager).length,
      uniqueDepartments: Object.keys(byDepartment).length,
      withReturn: withReturnTotal,
      withoutReturn: withoutReturnTotal,
      titleData,
      notAdiData,
      departmentData,
      managerData,
      returnPersonData,
      statusData,
      statusBuckets,
    };
  }, [notes, titleLimit, notAdiLimit, deptLimit]);

  const inputClass =
    'rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100';

  const filterBar = (
    <>
      <div className="rounded-2xl border border-slate-200/80 bg-gradient-to-r from-teal-500 to-cyan-500 px-6 py-5 text-white shadow-sm">
        <h1 className="text-2xl font-bold">Not Dashboard</h1>
        <p className="mt-1 text-sm text-teal-50">
          Koşulları seçip “Rapor Oluştur”a basın. Grafik veya kartlara tıklayarak ilgili notları görüntüleyin.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-1 text-xs font-medium text-slate-600 dark:text-slate-300">
            Not Tarihi (Başlangıç)
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={inputClass} />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-slate-600 dark:text-slate-300">
            Not Tarihi (Bitiş)
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={inputClass} />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-slate-600 dark:text-slate-300">
            Ara (başlık, not, kişi)
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') runReport(); }}
              placeholder="Anahtar kelime"
              className={inputClass}
            />
          </label>
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={runReport}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 disabled:opacity-50"
            >
              {loading ? 'Hazırlanıyor…' : 'Rapor Oluştur'}
            </button>
            {(dateFrom || dateTo || search) && (
              <button
                type="button"
                onClick={() => { setDateFrom(''); setDateTo(''); setSearch(''); }}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Temizle
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );

  if (error) {
    return (
      <div className="notes-dashboard nd-stack">
        {filterBar}
        <div className="nd-empty" style={{ borderColor: '#fecaca', color: '#b91c1c' }}>{error}</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="notes-dashboard nd-stack">
        {filterBar}
        <div className="nd-empty">Rapor hazırlanıyor…</div>
      </div>
    );
  }

  if (!hasRun) {
    return (
      <div className="notes-dashboard nd-stack">
        {filterBar}
        <div className="nd-empty">
          Rapor görüntülemek için tarih aralığını (veya arama) seçip “Rapor Oluştur”a basın.
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="notes-dashboard nd-stack">
        {filterBar}
        <div className="nd-empty">Seçilen kriterlere uygun not bulunamadı.</div>
      </div>
    );
  }

  const returnRate = analysis.total
    ? Math.round((analysis.withReturn / analysis.total) * 100)
    : 0;

  return (
    <div className="notes-dashboard nd-stack">
      {filterBar}

      <header className="nd-hero">
        <div className="nd-hero-inner">
          <h1>Not Analiz Paneli</h1>
          <p>
            Yönetici özeti: not yoğunluğu, departman performansı ve dönüş durumunu tek ekranda takip edin.
            Grafiklere veya kartlara tıklayarak ilgili notları anında görüntüleyin.
          </p>
          <div className="nd-hero-metrics">
            <div className="nd-hero-metric">
              <span>Toplam Not</span>
              <strong>{analysis.total}</strong>
            </div>
            <div className="nd-hero-metric">
              <span>Dönüş Oranı</span>
              <strong>%{returnRate}</strong>
            </div>
            <div className="nd-hero-metric">
              <span>Departman</span>
              <strong>{analysis.uniqueDepartments}</strong>
            </div>
            <div className="nd-hero-metric">
              <span>Bekleyen</span>
              <strong>{analysis.withoutReturn}</strong>
            </div>
          </div>
        </div>
      </header>

      <section className="nd-section">
        <h2 className="nd-section-title">Özet Göstergeler</h2>
        <div className="nd-kpi-grid">
          <KpiCard
            label="Toplam Not"
            value={analysis.total}
            accent="#0D9488"
            icon={ChartBarIcon}
            onClick={() => openDrill({ type: 'all', title: 'Tüm Notlar' })}
          />
          <KpiCard
            label="Dönüş Yapılan"
            value={analysis.withReturn}
            accent="#10B981"
            icon={CheckCircleIcon}
            onClick={() => openDrill({ type: 'withReturn', title: 'Dönüş Yapılan Notlar' })}
          />
          <KpiCard
            label="Dönüş Yapılmayan"
            value={analysis.withoutReturn}
            accent="#EF4444"
            icon={XCircleIcon}
            onClick={() => openDrill({ type: 'withoutReturn', title: 'Dönüş Yapılmayan Notlar' })}
          />
          <KpiCard label="Departman" value={analysis.uniqueDepartments} accent="#6366F1" icon={BuildingOffice2Icon} />
          <KpiCard label="Farklı Başlık" value={analysis.uniqueTitles} accent="#3B82F6" icon={DocumentTextIcon} />
          <KpiCard label="Farklı Not Adı" value={analysis.uniqueNotAdi} accent="#F97316" icon={FolderIcon} />
          <KpiCard
            label="Açık"
            value={analysis.statusBuckets.open}
            accent="#0EA5E9"
            icon={DocumentTextIcon}
            onClick={() => openDrill({ type: 'status', value: 'open', title: 'Açık Notlar' })}
          />
          <KpiCard label="Atanan Kişi" value={analysis.uniqueManagers} accent="#EC4899" icon={UsersIcon} />
        </div>
      </section>

      <section className="nd-section">
        <h2 className="nd-section-title">Departman Analizi</h2>
        <ChartCard
          title="Departman Bazlı Not ve Dönüş Durumu"
          subtitle="Yeşil: dönüş yapılan · Kırmızı: bekleyen notlar"
          actions={<LimitSelector value={deptLimit} onChange={setDeptLimit} activeColor="#6366F1" />}
        >
          <p className="nd-chart-tip">Çubuklara veya tablo satırlarına tıklayarak departman detayını açın.</p>
        {analysis.departmentData.length === 0 ? (
          <div className="nd-empty">Departman kaydı bulunamadı.</div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={analysis.departmentData}
                margin={{ left: 10, right: 20, top: 8, bottom: 70 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, angle: -34, textAnchor: 'end', fill: '#64748B' }}
                  tickFormatter={(v) => {
                    const t = translateDepartment(v);
                    return t && t.length > 20 ? `${t.slice(0, 20)}...` : t;
                  }}
                  interval={0}
                  height={85}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar
                  dataKey="withReturn"
                  name="Dönüş Yapılan"
                  stackId="dept"
                  fill="#10B981"
                  radius={[0, 0, 0, 0]}
                  barSize={40}
                  cursor="pointer"
                  onClick={(data) => openDrill({
                    type: 'department',
                    value: data.name,
                    returnStatus: 'with',
                    title: `${data.name} · Dönüş Yapılan`,
                  })}
                />
                <Bar
                  dataKey="withoutReturn"
                  name="Dönüş Yapılmayan"
                  stackId="dept"
                  fill="#F43F5E"
                  radius={[8, 8, 0, 0]}
                  barSize={40}
                  cursor="pointer"
                  onClick={(data) => openDrill({
                    type: 'department',
                    value: data.name,
                    returnStatus: 'without',
                    title: `${data.name} · Dönüş Yapılmayan`,
                  })}
                />
              </BarChart>
            </ResponsiveContainer>

            <div className="nd-dept-table">
              <table>
                <thead>
                  <tr>
                    <th>Departman</th>
                    <th>Toplam</th>
                    <th>Dönüş</th>
                    <th>Bekleyen</th>
                    <th>Oran</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.departmentData.map((row) => {
                    const rate = row.total ? Math.round((row.withReturn / row.total) * 100) : 0;
                    return (
                      <tr
                        key={row.name}
                        onClick={() => openDrill({
                          type: 'department',
                          value: row.name,
                          title: `${row.name} · Tüm Notlar`,
                        })}
                      >
                        <td className="font-semibold">{translateDepartment(row.name)}</td>
                        <td>{row.total}</td>
                        <td
                          className="text-emerald-600 dark:text-emerald-400"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDrill({
                              type: 'department',
                              value: row.name,
                              returnStatus: 'with',
                              title: `${row.name} · Dönüş Yapılan`,
                            });
                          }}
                        >
                          {row.withReturn}
                        </td>
                        <td
                          className="text-rose-600 dark:text-rose-400"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDrill({
                              type: 'department',
                              value: row.name,
                              returnStatus: 'without',
                              title: `${row.name} · Dönüş Yapılmayan`,
                            });
                          }}
                        >
                          {row.withoutReturn}
                        </td>
                        <td>
                          <div className="nd-rate-bar">
                            <div className="nd-rate-bar-track">
                              <div className="nd-rate-bar-fill" style={{ width: `${rate}%` }} />
                            </div>
                            <span className="text-xs font-bold">%{rate}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
        </ChartCard>
      </section>

      <section className="nd-section">
        <h2 className="nd-section-title">Başlık Analizi</h2>
        <ChartCard
          title="Not Başlığına Göre Madde Sayısı"
          subtitle="En kalabalık başlıkları tespit edin"
          actions={<LimitSelector value={titleLimit} onChange={setTitleLimit} activeColor="#0D9488" />}
        >
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={analysis.titleData} margin={{ left: 10, right: 20, top: 8, bottom: 70 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 10, angle: -34, textAnchor: 'end', fill: '#64748B' }}
              tickFormatter={(v) => (v && v.length > 20 ? `${v.slice(0, 20)}...` : v)}
              interval={0}
              height={85}
            />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="count"
              name="Madde"
              radius={[8, 8, 0, 0]}
              barSize={34}
              cursor="pointer"
              onClick={(data) => openDrill({
                type: 'title',
                value: data.name,
                title: `Başlık: ${data.name}`,
              })}
            >
              {analysis.titleData.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        </ChartCard>
      </section>

      <section className="nd-section">
        <h2 className="nd-section-title">Detay Grafikler</h2>
        <div className="nd-grid-2">
        <ChartCard
          title="İlgili Not Adına Göre Madde Sayısı"
          subtitle="Toplantı / günlük not grupları"
          actions={<LimitSelector value={notAdiLimit} onChange={setNotAdiLimit} activeColor="#F97316" />}
        >
          {analysis.notAdiData.length === 0 ? (
            <div className="nd-empty">İlgili not adı kaydı bulunamadı.</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(320, analysis.notAdiData.length * 32)}>
              <BarChart data={analysis.notAdiData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={200} tick={{ fontSize: 10 }} tickFormatter={(v) => (v && v.length > 28 ? `${v.slice(0, 28)}...` : v)} />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="count"
                  name="Madde"
                  radius={[0, 8, 8, 0]}
                  barSize={20}
                  cursor="pointer"
                  onClick={(data) => openDrill({
                    type: 'notAdi',
                    value: data.name,
                    title: `İlgili Not Adı: ${data.name}`,
                  })}
                >
                  {analysis.notAdiData.map((_, i) => (
                    <Cell key={i} fill={COLORS[(i + 5) % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Durum Dağılımı" subtitle="Açık · Kapatılan · Dönüş yapılan">
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={analysis.statusData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={72}
                outerRadius={120}
                labelLine={false}
                label={renderPieLabel}
                cursor="pointer"
                onClick={(data) => {
                  const statusKey = data.key || STATUS_LABEL_TO_KEY[data.name];
                  if (!statusKey) return;
                  openDrill({
                    type: 'status',
                    value: statusKey,
                    title: `Durum: ${data.name}`,
                  });
                }}
              >
                {analysis.statusData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
        </div>
      </section>

      <section className="nd-section">
        <h2 className="nd-section-title">Kişi Bazlı Analiz</h2>
        <div className="nd-grid-2">
        <ChartCard title="En Çok Madde Atanan Kişiler" subtitle="Departman yöneticisi iş yükü">
          <ResponsiveContainer width="100%" height={Math.max(320, analysis.managerData.length * 32)}>
            <BarChart data={analysis.managerData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 10 }} tickFormatter={(v) => (v && v.length > 24 ? `${v.slice(0, 24)}...` : v)} />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="count"
                name="Madde"
                fill="#6366F1"
                radius={[0, 8, 8, 0]}
                barSize={20}
                cursor="pointer"
                onClick={(data) => openDrill({
                  type: 'manager',
                  value: data.name,
                  title: `Atanan Kişi: ${data.name}`,
                })}
              >
                {analysis.managerData.map((_, i) => (
                  <Cell key={i} fill={COLORS[(i + 3) % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="En Çok Dönüş Yapan Kişiler" subtitle="Aktif dönüş veren kişiler">
          {analysis.returnPersonData.length === 0 ? (
            <div className="nd-empty">Henüz dönüş kaydı bulunamadı.</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(320, analysis.returnPersonData.length * 32)}>
              <BarChart data={analysis.returnPersonData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 10 }} tickFormatter={(v) => (v && v.length > 24 ? `${v.slice(0, 24)}...` : v)} />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="count"
                  name="Dönüş"
                  fill="#F59E0B"
                  radius={[0, 8, 8, 0]}
                  barSize={20}
                  cursor="pointer"
                  onClick={(data) => openDrill({
                    type: 'returnPerson',
                    value: data.name,
                    title: `Dönüş Yapan: ${data.name}`,
                  })}
                >
                  {analysis.returnPersonData.map((_, i) => (
                    <Cell key={i} fill={COLORS[(i + 6) % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        </div>
      </section>

      <ResponsiveModal
        isOpen={Boolean(drillFilter)}
        onClose={() => {
          setDrillFilter(null);
          closeNoteDetail();
        }}
        className="max-w-5xl"
      >
        <div className="nd-modal-head">
          <div>
            <h2>{drillFilter?.title || 'Not Detayı'}</h2>
            <p>
              {filteredDrillNotes.length === drillNotes.length
                ? `${drillNotes.length} not`
                : `${filteredDrillNotes.length} / ${drillNotes.length} not`}
              {' · '}Satıra tıklayarak notu açabilirsiniz
            </p>
          </div>
          <div className="nd-modal-actions">
            <Link to={buildNotesPageUrl(drillFilter)} className="nd-btn nd-btn-primary">
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              Günlük Notlarda Aç
            </Link>
            <button
              type="button"
              onClick={() => {
                setDrillFilter(null);
                closeNoteDetail();
              }}
              className="nd-btn nd-btn-ghost"
            >
              Kapat
            </button>
          </div>
        </div>

        <div className="nd-modal-body">
          {drillNotes.length === 0 ? (
            <div className="nd-empty">Bu seçime ait not bulunamadı.</div>
          ) : (
            <>
              <div className="nd-drill-toolbar">
                <input
                  type="search"
                  className="nd-search"
                  placeholder="Başlık, departman, kişi veya durum ara..."
                  value={drillSearch}
                  onChange={(e) => setDrillSearch(e.target.value)}
                />
                <div className="nd-chips">
                  <span className="nd-chip">Toplam: {drillSummary.total}</span>
                  <span className="nd-chip nd-chip--ok">Dönüş: {drillSummary.withReturn}</span>
                  <span className="nd-chip nd-chip--warn">Bekleyen: {drillSummary.withoutReturn}</span>
                  <span className="nd-chip">Oran: %{drillSummary.returnRate}</span>
                </div>
              </div>

              {filteredDrillNotes.length === 0 ? (
                <div className="nd-empty">Arama kriterine uygun not bulunamadı.</div>
              ) : (
                <div className="nd-note-list">
                  {filteredDrillNotes.map((note) => (
                    <button
                      key={note.id}
                      type="button"
                      className="nd-note-card"
                      onClick={() => openNoteDetail(note)}
                    >
                      <div className="nd-note-card-top">
                        <span className="nd-note-date">{formatDateDisplay(note.not_tarihi) || '-'}</span>
                        <span className={`nd-badge ${noteHasReturn(note) ? 'nd-badge--ok' : 'nd-badge--warn'}`}>
                          {noteHasReturn(note) ? 'Dönüş var' : 'Bekliyor'}
                        </span>
                      </div>
                      <h4 className="nd-note-title">{note.not_basligi || 'Başlıksız'}</h4>
                      <p className="nd-note-meta">
                        <span>{note.ilgili_not_adi || 'İlgili not adı yok'}</span>
                        <span>·</span>
                        <span>{note.ilgili_departman || 'Belirtilmemiş'}</span>
                        <span>·</span>
                        <span>{note.ilgili_kisi || '-'}</span>
                      </p>
                      {(note.not || note.donusler) && (
                        <p className="nd-note-snippet">
                          {truncate(note.not || note.donusler)}
                        </p>
                      )}
                      {note.durum && (
                        <span className="nd-note-status">{note.durum}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </ResponsiveModal>

      <ResponsiveModal
        isOpen={Boolean(selectedNote)}
        onClose={closeNoteDetail}
        className="max-w-5xl w-[min(100%,calc(100vw-2rem))]"
        overlayClassName="z-[1100]"
      >
        <div className="px-4 pt-5 pb-4 sm:p-6 max-h-[88vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Not Detayı
            </h2>
            <button
              type="button"
              onClick={closeNoteDetail}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {noteDetailLoading ? (
            <div className="rounded-md border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
              Not yükleniyor...
            </div>
          ) : noteDetailError ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
              {noteDetailError}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">İlgili Not Adı</Label>
                <Input value={selectedNote?.ilgili_not_adi || ''} readOnly />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Not Tarihi</Label>
                <Input value={formatDateDisplay(selectedNote?.not_tarihi)} readOnly placeholder="gg.aa.yyyy" />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Not Başlığı</Label>
                <Input value={selectedNote?.not_basligi || ''} readOnly />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Not</Label>
                <textarea
                  rows={8}
                  value={selectedNote?.not || ''}
                  readOnly
                  className={READONLY_TEXTAREA_CLASS}
                />
              </div>
              <ReadOnlyImages
                label="Not Görselleri"
                images={getNoteImages(selectedNote)}
                onOpenPreview={openImagePreview}
              />
              <ReadOnlyDocuments
                label="Not Dökümanları"
                documents={getNoteDocuments(selectedNote)}
              />
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Dönüşler</Label>
                <textarea
                  rows={7}
                  value={selectedNote?.donusler || ''}
                  readOnly
                  className={READONLY_TEXTAREA_CLASS}
                />
              </div>
              <ReadOnlyImages
                label="Dönüş Görselleri"
                images={getReturnImages(selectedNote)}
                onOpenPreview={openImagePreview}
              />
              <ReadOnlyDocuments
                label="Dönüş Dökümanları"
                documents={getReturnDocuments(selectedNote)}
              />
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">İlgili Departmanlar</Label>
                <Input value={selectedNote?.ilgili_departman || ''} readOnly />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">İlgili Kişiler</Label>
                <Input value={selectedNote?.ilgili_kisi || ''} readOnly />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Durum</Label>
                <Input value={selectedNote?.durum || ''} readOnly />
              </div>
            </div>
          )}
        </div>
      </ResponsiveModal>

      <ImagePreview
        images={previewImages}
        currentIndex={previewIndex}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        onNavigate={setPreviewIndex}
      />
    </div>
  );
}
